
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/MyContext';
import {
  CheckCircle,
  Rocket,
  Star,
  Award,
  Users,
  Clock,
  Check,
  Zap,
  Sparkles,
  Shield,
  Bell,
  DollarSign,
  TrendingUp,
  BarChart,
  Compass,
  Wrench,
  Video,
  Users as UsersIcon,
  BarChart3,
  Brain,
  FileCode,
  HeartHandshake,
  UserPlus,
  Lightbulb,
  Bolt,
} from 'lucide-react';

const SlotMachineCounter = ({ end, duration = 2000, icon: Icon, color = "blue", suffix = "" }: 
  { end: number | string, duration?: number, icon: React.ElementType, color?: string, suffix?: string }) => {
  const [count, setCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  
  useEffect(() => {
    let startTimestamp: number | null = null;
    
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      if (typeof end === 'number') {
        setCount(Math.floor(progress * end));
      }
      
      if (progress < 1) {
        if (typeof end === 'number' && Math.random() > 0.7) {
          setCount(Math.floor(Math.random() * end));
        }
        window.requestAnimationFrame(step);
      } else {
        setIsComplete(true);
      }
    };
    
    window.requestAnimationFrame(step);
  }, [end, duration]);
  
  const iconColorClasses: Record<string, string> = {
    "blue": "text-blue-500",
    "purple": "text-purple-500",
    "green": "text-green-500",
    "orange": "text-orange-500",
    "amber": "text-amber-500",
    "red": "text-red-500"
  };
  
  const bgColorClasses: Record<string, string> = {
    "blue": "bg-blue-100",
    "purple": "bg-purple-100",
    "green": "bg-green-100",
    "orange": "bg-orange-100",
    "amber": "bg-amber-100",
    "red": "bg-red-100"
  };
  
  // Determine the correct display based on the end value and suffix
  const getDisplayValue = (value: number | string, isEnd: boolean) => {
    if (typeof value === 'string') return value;
    
    const formattedNumber = value.toLocaleString();
    
    if (isEnd) {
      // Use the provided suffix or determine based on the end value
      return `${formattedNumber}${suffix}`;
    }
    
    // During animation, use the same suffix
    return `${formattedNumber}${suffix}`;
  };
  
  return (
    <div className="flex flex-col items-center">
      <div className={`mb-3 inline-block rounded-full ${bgColorClasses[color]} p-4 shadow-md`}>
        <Icon className={`h-8 w-8 ${iconColorClasses[color]}`} />
      </div>
      <span className="block font-bold text-4xl mb-2 text-gray-900 transition-all duration-200">
        {isComplete 
          ? getDisplayValue(end, true) 
          : getDisplayValue(count, false)}
      </span>
    </div>
  );
};

const Home = () => {
  const { user } = useAuth();
  
  const testimonials = [
    {
      text: "The AI app development tools are incredible. I've built and launched my first app in just 3 weeks with no coding experience.",
      author: "Sarah T.",
      role: "App Founder",
      initials: "ST",
      image: "https://randomuser.me/api/portraits/women/1.jpg"
    },
    {
      text: "The accelerator program was a game-changer. My app went from 100 to 10,000 users in just two months using the AI growth strategies.",
      author: "Michael R.",
      role: "Startup Founder",
      initials: "MR",
      image: "https://randomuser.me/api/portraits/men/2.jpg"
    },
    {
      text: "The Elite VIP service delivered beyond expectations. They built and launched my AI app in 4 weeks, and it's already generating revenue.",
      author: "Jennifer L.",
      role: "Business Owner",
      initials: "JL",
      image: "https://randomuser.me/api/portraits/women/3.jpg"
    },
    {
      text: "I was skeptical about using AI for app development, but this program made it incredibly easy. My app is now live and growing!",
      author: "David K.",
      role: "Entrepreneur",
      initials: "DK",
      image: "https://randomuser.me/api/portraits/men/4.jpg"
    },
    {
      text: "The 24/7 support team is amazing. They helped me solve complex issues at 2 AM before my app launch.",
      author: "Emily W.",
      role: "Tech Founder",
      initials: "EW",
      image: "https://randomuser.me/api/portraits/women/5.jpg"
    },
    {
      text: "AppLaunchAI's unlimited development option saved us thousands in development costs while delivering professional results.",
      author: "Robert J.",
      role: "CEO",
      initials: "RJ",
      image: "https://randomuser.me/api/portraits/men/6.jpg"
    },
    {
      text: "As a non-technical founder, I never thought I could build an app. Now I have three on the market thanks to this platform!",
      author: "Alicia M.",
      role: "Serial Entrepreneur",
      initials: "AM",
      image: "https://randomuser.me/api/portraits/women/7.jpg"
    },
    {
      text: "The monetization strategies helped me turn my free app into a $15k/month business in just 90 days.",
      author: "Thomas P.",
      role: "App Developer",
      initials: "TP",
      image: "https://randomuser.me/api/portraits/men/8.jpg"
    },
    {
      text: "The step-by-step roadmap eliminated all guesswork. I followed it exactly and my app launched on schedule.",
      author: "Lauren B.",
      role: "Product Manager",
      initials: "LB",
      image: "https://randomuser.me/api/portraits/women/9.jpg"
    },
    {
      text: "I've tried other app development courses, but AppLaunchAI's AI-powered approach is in a completely different league.",
      author: "Kevin S.",
      role: "Digital Marketer",
      initials: "KS",
      image: "https://randomuser.me/api/portraits/men/10.jpg"
    },
    {
      text: "The AI tools saved me at least 200 hours of work. What would have taken months took just weeks.",
      author: "Michelle D.",
      role: "SaaS Founder",
      initials: "MD",
      image: "https://randomuser.me/api/portraits/women/11.jpg"
    },
    {
      text: "As someone with zero tech background, I'm amazed I could build and monetize an app that users love.",
      author: "Brian T.",
      role: "First-time Founder",
      initials: "BT",
      image: "https://randomuser.me/api/portraits/men/12.jpg"
    },
    {
      text: "The Elite VIP package was worth every penny. My custom app has already made back the investment 3x over.",
      author: "Patricia C.",
      role: "Business Consultant",
      initials: "PC",
      image: "https://randomuser.me/api/portraits/women/13.jpg"
    },
    {
      text: "The AI templates and prompts saved me countless hours. My app went from idea to App Store in just 21 days.",
      author: "Nathan L.",
      role: "Indie Developer",
      initials: "NL",
      image: "https://randomuser.me/api/portraits/men/14.jpg"
    },
    {
      text: "AppLaunchAI's approach to market research helped me pivot early and avoid a costly mistake before development.",
      author: "Vanessa R.",
      role: "Startup Founder",
      initials: "VR",
      image: "https://randomuser.me/api/portraits/women/15.jpg"
    },
  ];
  
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % (testimonials.length - 2));
    }, 4000);
    
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="flex flex-col min-h-screen">
      <section className="bg-gradient-to-br from-blue-50 to-purple-100 py-20 lg:py-32">
        <div className="container mx-auto px-4 text-center">
          <h1 className="mb-6 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tighter text-gray-900">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
              Build, Launch and Scale Your<br />
              App Idea with AI!
            </span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-xl text-gray-600">
            The Ultimate Membership for AI-Powered App Development & Growth. Turn your app idea into reality with expert guidance, AI-driven strategies, and exclusive unlimited app development.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/pricing">
              <Button size="lg" className="font-semibold bg-orange-500 hover:bg-orange-600 text-white">
                View All Plans
              </Button>
            </Link>
            <Link to={user ? '/dashboard' : '/login'}>
              <Button size="lg" className="font-semibold bg-orange-500 hover:bg-orange-600 text-white">
                {user ? 'Go to Dashboard' : 'Get Started'}
              </Button>
            </Link>
          </div>

          <div className="mt-16 flex flex-wrap items-center justify-center gap-16 text-center">
            <div className="text-gray-600 text-sm">
              <SlotMachineCounter end={1000} icon={Users} color="blue" suffix="+" />
              <span className="mt-2 block">Active Members</span>
            </div>
            <div className="text-gray-600 text-sm">
              <SlotMachineCounter end={98} icon={CheckCircle} color="green" suffix="%" />
              <span className="mt-2 block">Satisfaction Rate</span>
            </div>
            <div className="text-gray-600 text-sm">
              <SlotMachineCounter end="24/7" icon={Clock} color="purple" />
              <span className="mt-2 block">Support</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="mb-4 text-center text-3xl font-bold text-gray-900 md:text-4xl">
            Why Join AppLaunchAI?
          </h2>
          <p className="mx-auto mb-12 max-w-3xl text-center text-lg text-gray-600">
            Most founders waste months (or years) and thousands of dollars trying to figure out app development. Our membership gives you the AI-powered blueprint to shortcut the process, avoid costly mistakes, and scale smarter.
          </p>
          
          <div className="grid gap-8 md:grid-cols-3">
            <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 p-8 shadow-lg border border-blue-100 transition-transform hover:-translate-y-1 hover:shadow-xl duration-300">
              <div className="mb-6 inline-block rounded-full bg-blue-100 p-4 shadow-md">
                <Rocket className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-blue-800">AI-Powered Roadmap</h3>
              <p className="text-gray-700">
                Follow our proven step-by-step guidance from idea validation through design, development, and launch. Our AI-powered roadmap eliminates guesswork and accelerates your journey to success.
              </p>
            </div>
            
            <div className="rounded-xl bg-gradient-to-br from-purple-50 to-indigo-50 p-8 shadow-lg border border-purple-100 transition-transform hover:-translate-y-1 hover:shadow-xl duration-300">
              <div className="mb-6 inline-block rounded-full bg-purple-100 p-4 shadow-md">
                <Sparkles className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-purple-800">Exclusive AI Tools</h3>
              <p className="text-gray-700">
                Access premium AI tools, templates for business planning, monetization strategies & more. Leverage cutting-edge technology to give your app the competitive edge it needs.
              </p>
            </div>
            
            <div className="rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 p-8 shadow-lg border border-orange-100 transition-transform hover:-translate-y-1 hover:shadow-xl duration-300">
              <div className="mb-6 inline-block rounded-full bg-orange-100 p-4 shadow-md">
                <Zap className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-orange-800">Unlimited Development</h3>
              <p className="text-gray-700">
                Exclusive unlimited monthly app development services to launch your app. Get dedicated support from our expert developers who will build, test, and deploy your application without the typical agency price tag.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="mb-12 text-center text-3xl font-bold text-gray-900 md:text-4xl">
            Get Everything You Need to Succeed
          </h2>
          
          <div className="grid gap-y-10 md:grid-cols-2 md:gap-x-12">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <Brain className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2 text-left">AI-Powered App Building Roadmap</h3>
                <p className="text-gray-600 text-left">Follow our proven step-by-step guidance from initial idea validation through design, development, and all the way to successful launch. Our AI-powered roadmap eliminates guesswork and accelerates your journey.</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <FileCode className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2 text-left">Exclusive AI Tools & Templates</h3>
                <p className="text-gray-600 text-left">Gain immediate access to our suite of premium AI tools and templates for business planning, monetization strategy development, user acquisition campaigns, and investor-ready funding pitch decks.</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <HeartHandshake className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2 text-left">Live Expert Coaching & Q&A</h3>
                <p className="text-gray-600 text-left">Connect directly with our team of experienced app developers and entrepreneurs in regular live sessions. Get personalized feedback on your specific app challenges and receive tailored guidance for your unique journey.</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <UserPlus className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2 text-left">Private Founder Community</h3>
                <p className="text-gray-600 text-left">Join our exclusive network of like-minded app entrepreneurs where you can share ideas, overcome challenges together, and form valuable partnerships. Tap into the collective wisdom of founders at every stage of the app journey.</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <Lightbulb className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2 text-left">AI-Powered Growth Hacks</h3>
                <p className="text-gray-600 text-left">Implement cutting-edge automated marketing systems, user acquisition strategies, and app store optimization techniques powered by AI. Our proven growth hacks help you attract users faster and more efficiently.</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Bolt className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2 text-left">Fast-Track App Success</h3>
                <p className="text-gray-600 text-left">Skip months of trial and error with our streamlined approach to app development and marketing. Build and launch your successful app without coding knowledge or expensive agencies, saving both time and money.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <h2 className="mb-4 text-center text-3xl font-bold text-gray-900 md:text-4xl">
            Membership Benefits That Save You Time & Money
          </h2>
          <p className="mx-auto mb-12 max-w-3xl text-center text-lg text-gray-600">
            Our AI-powered approach slashes development time and costs while maximizing your app's potential
          </p>
          
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl bg-white p-8 shadow-md border-t-4 border-amber-400 hover:shadow-xl transition-shadow">
              <div className="mb-6 inline-block rounded-full bg-amber-100 p-4">
                <Clock className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold">Save Months of Trial & Error</h3>
              <p className="text-gray-700">
                Our proven framework helps you launch faster by avoiding common pitfalls that delay most app developers. What takes others months can be accomplished in weeks with our streamlined approach.
              </p>
            </div>
            
            <div className="rounded-xl bg-white p-8 shadow-md border-t-4 border-green-400 hover:shadow-xl transition-shadow">
              <div className="mb-6 inline-block rounded-full bg-green-100 p-4">
                <DollarSign className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold">Avoid Costly Mistakes</h3>
              <p className="text-gray-700">
                Our AI tools help prevent budget blowouts and development dead-ends by identifying potential issues before they become expensive problems. Save thousands in development costs with smart planning.
              </p>
            </div>
            
            <div className="rounded-xl bg-white p-8 shadow-md border-t-4 border-blue-400 hover:shadow-xl transition-shadow">
              <div className="mb-6 inline-block rounded-full bg-blue-100 p-4">
                <TrendingUp className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold">Monetize & Scale Faster</h3>
              <p className="text-gray-700">
                Start generating revenue sooner with our AI-driven pricing strategies, growth frameworks, and user retention tactics implemented from day one. Turn your app into a sustainable business faster than your competitors.
              </p>
            </div>
            
            <div className="rounded-xl bg-white p-8 shadow-md border-t-4 border-purple-400 hover:shadow-xl transition-shadow">
              <div className="mb-6 inline-block rounded-full bg-purple-100 p-4">
                <BarChart className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold">Stay Ahead of Trends</h3>
              <p className="text-gray-700">
                Keep your app on the cutting edge with our continuously updated library of AI strategies used by top startups. Access emerging techniques for sustainable growth and innovation before your competitors.
              </p>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <Link to="/pricing">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white py-3 px-8 text-lg font-medium">
                Get Started Today
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <h2 className="mb-12 text-center text-3xl font-bold text-gray-900 md:text-4xl">
            What Our Members Say
          </h2>
          
          <div className="testimonial-slider relative max-w-6xl mx-auto">
            <div className="overflow-hidden">
              <div 
                className="flex transition-all duration-1000 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 33.333}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div 
                    key={index} 
                    className="min-w-full md:min-w-[33.333%] px-3"
                  >
                    <div className="h-full rounded-lg bg-white p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                      <div className="mb-4 flex items-center">
                        <div className="flex text-amber-500">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="h-4 w-4 fill-current" />
                          ))}
                        </div>
                      </div>
                      <p className="mb-6 text-gray-600 italic">
                        "{testimonial.text}"
                      </p>
                      <div className="flex items-center">
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.author}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                        <div className="ml-3">
                          <p className="font-semibold">{testimonial.author}</p>
                          <p className="text-sm text-gray-500">{testimonial.role}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 flex justify-center space-x-2">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, i) => (
                <button
                  key={i}
                  className={`h-3 w-3 rounded-full transition-all ${
                    Math.floor(currentIndex / 3) === i ? 'bg-blue-600 w-8' : 'bg-gray-300'
                  }`}
                  onClick={() => setCurrentIndex(i * 3)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-6 text-3xl font-bold md:text-4xl">
            Ready to Launch Your AI App?
          </h2>
          <p className="mx-auto mb-8 max-w-2xl text-xl opacity-90">
            Choose the membership level that's right for you and begin your app development journey today.
          </p>
          <Link to="/pricing">
            <Button size="lg" className="font-semibold bg-orange-500 hover:bg-orange-600 text-white">
              View Pricing Plans
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
